import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import SectionGuard from '../common/SectionGuard';
import SkeletonImage from '../common/SkeletonImage';

const IndustrySolutions = ({ industries = [], block2 = [], apiUrl = '' }) => {
  const intl = useIntl();
  const displayIndustries = block2 && block2.length > 0 ? block2 : industries;
  const finalDisplayIndustries = displayIndustries.length > 0 ? displayIndustries : [];
  const hasContent = finalDisplayIndustries.length > 0;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex >= finalDisplayIndustries.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, finalDisplayIndustries.length]);

  const resolveImage = (industry) => {
    let rawIndustryImage;
    if (industry.image && typeof industry.image === 'object') {
      rawIndustryImage =
        industry.image.formats?.large?.url ||
        industry.image.formats?.medium?.url ||
        industry.image.formats?.small?.url ||
        industry.image.url ||
        industry.image.data?.attributes?.formats?.large?.url ||
        industry.image.data?.attributes?.formats?.medium?.url ||
        industry.image.data?.attributes?.url;
    } else {
      rawIndustryImage = industry.image;
    }
    return apiUrl && rawIndustryImage && rawIndustryImage.startsWith('/')
      ? `${apiUrl}${rawIndustryImage}`
      : rawIndustryImage;
  };

  const resolveIcon = (industry) => {
    let industryIcon;
    if (industry.icon && typeof industry.icon === 'object') {
      industryIcon = industry.icon.url || industry.icon.data?.attributes?.url;
    } else {
      industryIcon = industry.icon;
    }
    return apiUrl && industryIcon && industryIcon.startsWith('/')
      ? `${apiUrl}${industryIcon}`
      : industryIcon;
  };

  const active = finalDisplayIndustries[activeIndex] || null;
  const activeImage = active ? resolveImage(active) : '';
  const activeIcon = active ? resolveIcon(active) : '';

  const goPrev = () => {
    setActiveIndex((i) => (i === 0 ? finalDisplayIndustries.length - 1 : i - 1));
  };

  const goNext = () => {
    setActiveIndex((i) => (i === finalDisplayIndustries.length - 1 ? 0 : i + 1));
  };

  return (
    <SectionGuard hasContent={hasContent} emptyMessageId="empty.section">
      <section className="relative bg-[#0a1628] text-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pt-20 lg:pt-28 pb-8 lg:pb-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-14">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-px bg-[#16A34A]" />
                <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#16A34A]">
                  Solutions
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">
                {intl.formatMessage({
                  id: 'explore.industries',
                  defaultMessage: 'Explore Africa Systems Industry Solutions'
                })}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={goPrev}
                className="w-11 h-11 grid place-items-center border border-white/20 text-white/70 hover:border-[#16A34A] hover:text-white transition-colors"
                aria-label="Previous sector"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={goNext}
                className="w-11 h-11 grid place-items-center border border-white/20 text-white hover:border-[#16A34A] hover:bg-[#16A34A] transition-colors"
                aria-label="Next sector"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <span className="ml-2 text-white/40 text-xs tracking-[0.2em] tabular-nums">
                {String(activeIndex + 1).padStart(2, '0')}
                <span className="mx-1.5 text-white/25">/</span>
                {String(finalDisplayIndustries.length).padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[520px] lg:min-h-[620px]">
          {/* Navigation secteurs */}
          <div className="lg:col-span-4 border-t border-white/10 lg:border-t-0 lg:border-r border-white/10">
            <div className="max-h-[320px] lg:max-h-none overflow-y-auto lg:overflow-visible">
              {finalDisplayIndustries.map((industry, index) => {
                const isActive = index === activeIndex;
                return (
                  <button
                    key={industry.id || index}
                    onClick={() => setActiveIndex(index)}
                    className={`w-full text-left px-5 sm:px-8 lg:px-12 py-5 lg:py-6 border-b border-white/10 transition-colors duration-300 ${
                      isActive
                        ? 'bg-white/[0.06] text-white'
                        : 'text-white/45 hover:text-white/80 hover:bg-white/[0.03]'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span
                        className={`text-xs tabular-nums tracking-widest ${
                          isActive ? 'text-[#16A34A]' : 'text-white/25'
                        }`}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span
                        className={`text-sm sm:text-base font-semibold uppercase tracking-wide leading-snug ${
                          isActive ? 'text-white' : ''
                        }`}
                      >
                        {industry.title}
                      </span>
                      {isActive && (
                        <span className="ml-auto w-8 h-px bg-[#16A34A] hidden sm:block" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Visuel immersif */}
          <div className="lg:col-span-8 relative min-h-[420px] lg:min-h-full">
            {activeImage ? (
              <SkeletonImage
                key={activeImage}
                src={activeImage}
                alt={active?.title || ''}
                className="absolute inset-0"
                imgClassName="w-full h-full object-cover"
                onDark
              />
            ) : (
              <div className="absolute inset-0 bg-[#133618]" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/95 via-[#0a1628]/35 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/40 to-transparent" />

            <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-10 lg:p-14">
              {activeIcon ? (
                <SkeletonImage
                  src={activeIcon}
                  alt=""
                  className="w-10 h-10 mb-5 opacity-90"
                  imgClassName="w-full h-full object-contain"
                  imgStyle={{
                    filter:
                      'brightness(0) saturate(100%) invert(43%) sepia(87%) saturate(356%) hue-rotate(59deg) brightness(91%) contrast(88%)'
                  }}
                  icon={false}
                  onDark
                />
              ) : null}
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight max-w-lg leading-tight">
                {active?.title}
              </h3>
              {active?.description ? (
                <p className="mt-4 text-white/70 text-sm sm:text-base max-w-md leading-relaxed line-clamp-3">
                  {active.description}
                </p>
              ) : null}
              {active?.link ? (
                <a
                  href={active.link}
                  className="mt-7 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#16A34A] hover:text-white transition-colors group w-fit"
                >
                  {intl.formatMessage({ id: 'learn.more', defaultMessage: 'En savoir plus' })}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </SectionGuard>
  );
};

export default IndustrySolutions;
